import {
  faExclamationTriangle,
  faCheck,
  faTrash,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { getNotificationItemInfo } from "../helpers/Converters";
import { extraLongDelay, getErrors, longDelay, mediumDelay, shortDelay } from "../helpers/JSONapi";

const baseURL = `${process.env.REACT_APP_GOLFSTATUS_BASE_API_URL}`;

export const usersURL = `${baseURL}v2/users`;
export const v3UsersURL = `${baseURL}v3/users`;
export const facilities = `${baseURL}v2/facilities`;
export const organizationUsers = `${baseURL}v2/organization-users`;
export const discountCodes = `${baseURL}v2/discount-codes`;
export const organizations = `${baseURL}v2/organizations`;
export const v3Organizations = `${baseURL}v3/organizations`;
export const organizationSettings = `${baseURL}v2/organization-settings`;
export const organizationLinks = `${baseURL}v2/organization-additional-links`;
export const accessGroups = `${baseURL}v2/access-groups`;
export const accessGroupMembers = `${baseURL}v2/access-group-members`;
export const accessMembers = `${baseURL}v2/access-members`;
export const fileUplaod = `${baseURL}v2/file`;

// Thunk Responses
const getName = (thunk) => {
  if(thunk?.typePrefix?.includes?.("/")){
    return thunk?.typePrefix?.split?.("/")?.[1]
  }
  else return thunk.typePrefix
}

export const thunkResponse = (thunk, callback, name, notifications) => {
  return {
    [thunk.pending]: (state, action) => {
      if(notifications?.pendingText){
        state.notifications = [
          {
            state: "grey",
            header: getNotificationItemInfo(notifications?.pendingText, faSpinner),
            timeout: notifications?.pendingDelay,
            result: "pending"
          },
        ];
      }
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      if(notifications?.fulfilledText){
        state.notifications = [
          {
            header: getNotificationItemInfo(notifications.fulfilledText, faCheck),
            state: "success",
            result: "saved",
            resultPayload: action.payload,
            name: name,
            timeout: notifications.fulfilledDelay
          },
        ];
      }
      
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback?.(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      if(notifications?.rejectedDelay){
        state.notifications = [
          {
            header: getNotificationItemInfo(
              getErrors(action),
              faExclamationTriangle
            ),
            result: "error",
            state: "warning",
            timeout: notifications.rejectedDelay,
          },
        ];
      }
    },
  };
};

// THUNK responses from the api
export const getThunkResponse = (thunk, callback) => {
  return {
    [thunk.pending]: (state, action) => {
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      state.notifications = [
        {
          header: getNotificationItemInfo(
            getErrors(action),
            faExclamationTriangle
          ),
          state: "warning",
          timeout: longDelay,
        },
      ];
    },
  };
};

export const createThunkResponse = (thunk, callback, name, timeout) => {
  return {
    [thunk.pending]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`Saving ${name}`, faSpinner),
          timeout: timeout ? timeout : shortDelay,
          result: "pending"
        },
      ];
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`${name} has been created!`, faCheck),
          state: "success",
          result: "created",
          resultPayload: action.payload,
          name: name,
          timeout: shortDelay,
        },
      ];
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback?.(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      state.notifications = [
        {
          header: getNotificationItemInfo(
            getErrors(action),
            faExclamationTriangle
          ),
          state: "warning",
          timeout: longDelay,
          result: "error"
        },
      ];
    },
  };
};

export const saveThunkResponse = (thunk, callback, name, timeout) => {
  return {
    [thunk.pending]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`Saving ${name}`, faSpinner),
          timeout: timeout ? timeout : shortDelay,
          result: "pending"
        },
      ];
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`${name} has been saved!`, faCheck),
          state: "success",
          result: "saved",
          resultPayload: action.payload,
          name: name,
          timeout: mediumDelay
        },
      ];
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback?.(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      state.notifications = [
        {
          header: getNotificationItemInfo(
            getErrors(action),
            faExclamationTriangle
          ),
          result: "error",
          state: "warning",
          timeout: longDelay,
        },
      ];
    },
  };
};

export const deleteThunkResponse = (thunk, callback, name, timeout) => {
  return {
    [thunk.pending]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`Removing ${name}`, faSpinner),
          timeout: timeout ? timeout : shortDelay,
        },
      ];
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`${name} has been removed!`, faTrash),
          state: "success",
          result: "removed",
          timeout: shortDelay,
          name: name,
        },
      ];
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback?.(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      state.notifications = [
        {
          header: getNotificationItemInfo(
            getErrors(action),
            faExclamationTriangle
          ),
          state: "warning",
          timeout: longDelay,
        },
      ];
    },
  };
};

export const uploadThunkResponse = (thunk, callback, name, timeout) => {
  return {
    [thunk.pending]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`Uploading ${name}`, faSpinner),
          timeout: timeout,
        },
      ];
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`${name} has been uploaded!`, faCheck),
          state: "success",
          result: "uploaded",
          resultPayload: action.payload,
          name: name,
          timeout: timeout,
        },
      ];
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback?.(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      state.notifications = [
        {
          header: getNotificationItemInfo(
            getErrors(action),
            faExclamationTriangle
          ),
          state: "warning",
          timeout: shortDelay,
        },
      ];
    },
  };
};

export const validateThunkResponse = (thunk, callback, name, timeout) => {
  return {
    [thunk.pending]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`Validating ${name}`, faSpinner),
          timeout: timeout,
        },
      ];
      state.loading.push(getName(thunk));
    },
    [thunk.fulfilled]: (state, action) => {
      state.notifications = [
        {
          header: getNotificationItemInfo(`${name} has been validated!`, faCheck),
          state: "success",
          result: "validated",
          resultPayload: action.payload,
          name: name,
          timeout: timeout,
        },
      ];
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      callback?.(state, action);
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter(
        (f) => f !== getName(thunk)
      );
      state.notifications = [
        {
          header: getNotificationItemInfo(
            getErrors(action),
            faExclamationTriangle
          ),
          state: "warning",
          timeout: shortDelay,
        },
      ];
    },
  };
};

export const fileDownloadThunkResponse = (thunk, nameFunction) => {
  return {
    [thunk.fulfilled]: (state, action) => {
      const filename = nameFunction?.(state, action);
      let blob = new Blob([action.payload.data]);
      if (window.navigator.msSaveBlob) {
        // IE
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        let windowUrl = window.URL || window.webkitURL;
        let fileUrl = windowUrl.createObjectURL(blob);
        let a = document.createElement("a");
        a.href = fileUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(function() {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(fileUrl);
        }, 0);
      }
      state.notifications = [
        {
          header: getNotificationItemInfo(`File has been successfully downloaded.`, faCheck),
          state: "success",
          timeout: shortDelay,
        },
      ];
      state.loading = state.loading.filter((f) => f !== getName(thunk));
    },
    [thunk.pending]: (state, action) => {
      state.loading.push(getName(thunk));
      state.notifications = [
        {
          header: getNotificationItemInfo(`Downloading file... this could take a second.`, faSpinner),
          timeout: extraLongDelay,
        },
      ];
    },
    [thunk.rejected]: (state, action) => {
      state.loading = state.loading.filter((f) => f !== getName(thunk));
    }
  }
}